import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Users, UserCheck, LogIn, LogOut, Clock, Plus, TrendingUp,
  Timer, AlertTriangle, RefreshCw, Loader2
} from "lucide-react";
import { ComposedChart, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Bar, PieChart, Pie, Cell, Legend } from "recharts";
import { ViewOnlyBanner } from "./ViewOnlyBanner";

export const UsersTab = ({
  darkMode, userSessions,
  fetchUserSessions, sessionTimeoutSetting,
  updateSessionTimeout, saving, viewOnly,
  signupAnalytics, roleAnalytics,
}) => {
  const cardBg = darkMode ? "bg-gray-800/80 border border-gray-700/50" : "";
  const innerCardBg = darkMode ? "bg-gray-700/50 border border-gray-600/50" : "";
  const valText = darkMode ? "text-white" : "text-navy-900";
  const lblText = darkMode ? "text-gray-400" : "text-gray-500";
  const subText = darkMode ? "text-gray-500" : "text-gray-400";

  return (
    <>
      {viewOnly && <ViewOnlyBanner darkMode={darkMode} />}
      <Card className={`border-0 shadow-soft rounded-3xl ${cardBg}`}>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className={`flex items-center gap-2 ${darkMode ? "text-gray-100" : ""}`}><Users className={`w-5 h-5 ${darkMode ? "text-blue-400" : "text-primary"}`} /> User Session Analytics</CardTitle>
          <Button variant="outline" size="sm" className={`rounded-xl ${darkMode ? "bg-gray-700 border-gray-600 text-gray-200 hover:bg-gray-600" : ""}`} onClick={fetchUserSessions} data-testid="refresh-user-sessions">
            <RefreshCw className="w-4 h-4 mr-2" /> Refresh
          </Button>
        </CardHeader>
        <CardContent>
          {userSessions ? (
            <div className="space-y-6">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {[
                  { label: "Total Users", value: userSessions.summary.total_users, icon: Users, color: "text-blue-600 bg-blue-50", darkColor: "text-blue-400 bg-blue-900/30" },
                  { label: "Currently Active", value: userSessions.summary.currently_active, icon: UserCheck, color: "text-green-600 bg-green-50", darkColor: "text-green-400 bg-green-900/30", description: "Active in last 15 mins" },
                  { label: "Logged In Today", value: userSessions.summary.logged_in_today, icon: LogIn, color: "text-emerald-600 bg-emerald-50", darkColor: "text-emerald-400 bg-emerald-900/30" },
                  { label: "Logged In This Week", value: userSessions.summary.logged_in_this_week, icon: Clock, color: "text-indigo-600 bg-indigo-50", darkColor: "text-indigo-400 bg-indigo-900/30" },
                ].map(({ label, value, icon: Icon, color, darkColor, description }) => {
                  const c = darkMode ? darkColor : color;
                  return (
                    <Card key={label} className={`border-0 shadow-sm rounded-2xl overflow-hidden ${innerCardBg}`}>
                      <CardContent className="p-5">
                        <div className={`w-10 h-10 rounded-xl ${c.split(" ")[1]} flex items-center justify-center mb-3`}><Icon className={`w-5 h-5 ${c.split(" ")[0]}`} /></div>
                        <p className={`text-2xl font-bold ${valText}`}>{value}</p>
                        <p className={`text-sm ${lblText}`}>{label}</p>
                        {description && <p className={`text-xs mt-1 ${subText}`}>{description}</p>}
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {[
                  { label: "Logged Out Today", value: userSessions.summary.logged_out_today, icon: LogOut, color: "text-orange-600 bg-orange-50", darkColor: "text-orange-400 bg-orange-900/30" },
                  { label: "Logged Out This Week", value: userSessions.summary.logged_out_this_week, icon: LogOut, color: "text-amber-600 bg-amber-50", darkColor: "text-amber-400 bg-amber-900/30" },
                  { label: "Signups Today", value: userSessions.summary.signups_today, icon: Plus, color: "text-pink-600 bg-pink-50", darkColor: "text-pink-400 bg-pink-900/30" },
                  { label: "Signups This Week", value: userSessions.summary.signups_this_week, icon: TrendingUp, color: "text-purple-600 bg-purple-50", darkColor: "text-purple-400 bg-purple-900/30" },
                ].map(({ label, value, icon: Icon, color, darkColor }) => {
                  const c = darkMode ? darkColor : color;
                  return (
                    <Card key={label} className={`border-0 shadow-sm rounded-2xl overflow-hidden ${innerCardBg}`}>
                      <CardContent className="p-5">
                        <div className={`w-10 h-10 rounded-xl ${c.split(" ")[1]} flex items-center justify-center mb-3`}><Icon className={`w-5 h-5 ${c.split(" ")[0]}`} /></div>
                        <p className={`text-2xl font-bold ${valText}`}>{value}</p>
                        <p className={`text-sm ${lblText}`}>{label}</p>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
              <div className="grid md:grid-cols-2 gap-6">
                <Card className={`border-0 shadow-sm rounded-2xl ${innerCardBg}`}>
                  <CardHeader className="pb-2"><CardTitle className={`text-base flex items-center gap-2 ${darkMode ? "text-gray-100" : ""}`}><div className="w-2 h-2 rounded-full bg-green-500 animate-pulse"></div>Currently Active ({userSessions.active_users.length})</CardTitle></CardHeader>
                  <CardContent>
                    {userSessions.active_users.length > 0 ? (
                      <div className="space-y-3 max-h-64 overflow-y-auto">
                        {userSessions.active_users.map((u, i) => (
                          <div key={u.id || i} className={`flex items-center justify-between rounded-xl p-3 ${darkMode ? "bg-green-900/20" : "bg-green-50"}`}>
                            <div className="flex items-center gap-3">
                              <div className={`w-8 h-8 rounded-full flex items-center justify-center ${darkMode ? "bg-green-900/30" : "bg-green-100"}`}><UserCheck className={`w-4 h-4 ${darkMode ? "text-green-400" : "text-green-600"}`} /></div>
                              <div><p className={`text-sm font-medium ${darkMode ? "text-gray-200" : ""}`}>{u.name || "User"}</p><p className={`text-xs ${subText}`}>{u.email}</p></div>
                            </div>
                            <div className="text-right">
                              <Badge variant="default" className="bg-green-500 rounded-full text-xs">Online</Badge>
                              <p className={`text-xs mt-1 ${subText}`}>{u.last_active ? new Date(u.last_active).toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" }) : "-"}</p>
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : <p className={`text-sm text-center py-4 ${subText}`}>No users currently active</p>}
                  </CardContent>
                </Card>
                <Card className={`border-0 shadow-sm rounded-2xl ${innerCardBg}`}>
                  <CardHeader className="pb-2"><CardTitle className={`text-base flex items-center gap-2 ${darkMode ? "text-gray-100" : ""}`}><LogOut className="w-4 h-4 text-orange-500" />Recently Logged Out ({userSessions.recently_logged_out.length})</CardTitle></CardHeader>
                  <CardContent>
                    {userSessions.recently_logged_out.length > 0 ? (
                      <div className="space-y-3 max-h-64 overflow-y-auto">
                        {userSessions.recently_logged_out.map((u, i) => (
                          <div key={u.id || i} className={`flex items-center justify-between rounded-xl p-3 ${darkMode ? "bg-orange-900/20" : "bg-orange-50"}`}>
                            <div className="flex items-center gap-3">
                              <div className={`w-8 h-8 rounded-full flex items-center justify-center ${darkMode ? "bg-orange-900/30" : "bg-orange-100"}`}><LogOut className={`w-4 h-4 ${darkMode ? "text-orange-400" : "text-orange-600"}`} /></div>
                              <div><p className={`text-sm font-medium ${darkMode ? "text-gray-200" : ""}`}>{u.name || "User"}</p><p className={`text-xs ${subText}`}>{u.email}</p></div>
                            </div>
                            <div className="text-right">
                              <Badge variant="secondary" className="rounded-full text-xs">Offline</Badge>
                              <p className={`text-xs mt-1 ${subText}`}>{u.logged_out_at ? new Date(u.logged_out_at).toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" }) : "-"}</p>
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : <p className={`text-sm text-center py-4 ${subText}`}>No recent logouts today</p>}
                  </CardContent>
                </Card>
              </div>
              {/* Session Timeout */}
              {!viewOnly && (
              <Card className={`border-2 rounded-2xl ${darkMode ? "border-indigo-700/50 bg-indigo-900/20" : "border-indigo-200 bg-indigo-50/30"}`}>
                <CardContent className="p-5">
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center gap-3">
                      <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${darkMode ? "bg-indigo-900/40" : "bg-indigo-100"}`}><Timer className={`w-6 h-6 ${darkMode ? "text-indigo-400" : "text-indigo-600"}`} /></div>
                      <div>
                        <h3 className={`font-bold text-lg ${darkMode ? "text-gray-100" : ""}`}>Session Timeout</h3>
                        <p className={`text-sm ${darkMode ? "text-gray-300" : "text-gray-500"}`}>Auto-logout users after period of inactivity</p>
                      </div>
                    </div>
                    <Badge className={`rounded-full ${sessionTimeoutSetting > 0 ? 'bg-green-500 text-white' : 'bg-gray-400 text-white'}`}>
                      {sessionTimeoutSetting > 0 ? `${sessionTimeoutSetting} min` : 'Disabled'}
                    </Badge>
                  </div>
                  <div className="grid grid-cols-4 gap-3">
                    {[{ value: 15, label: "15 min", description: "Strict" }, { value: 30, label: "30 min", description: "Balanced" }, { value: 60, label: "60 min", description: "Relaxed" }, { value: 0, label: "Off", description: "No timeout" }].map(({ value, label, description }) => (
                      <button key={value} onClick={() => updateSessionTimeout(value)} disabled={saving}
                        className={`p-4 rounded-xl border-2 transition-all ${
                          sessionTimeoutSetting === value
                            ? (darkMode ? 'border-indigo-500 bg-indigo-900/40 ring-2 ring-indigo-500/50' : 'border-indigo-500 bg-indigo-100 ring-2 ring-indigo-300')
                            : (darkMode ? 'border-gray-600 bg-gray-700/50 hover:border-indigo-500/50 hover:bg-gray-700' : 'border-gray-200 bg-white hover:border-indigo-300 hover:bg-indigo-50')
                        }`} data-testid={`session-timeout-${value}`}>
                        <p className={`text-lg font-bold ${darkMode ? "text-gray-100" : "text-gray-800"}`}>{label}</p>
                        <p className={`text-xs ${darkMode ? "text-gray-400" : "text-gray-500"}`}>{description}</p>
                      </button>
                    ))}
                  </div>
                  <p className={`text-xs mt-4 ${darkMode ? "text-gray-300" : "text-gray-500"}`}>
                    <AlertTriangle className={`w-3 h-3 inline mr-1 ${darkMode ? "text-amber-400" : ""}`} />
                    Users will be automatically logged out after the specified period of inactivity. A warning is shown 2 minutes before timeout.
                  </p>
                </CardContent>
              </Card>
              )}
            </div>
          ) : <div className="flex justify-center py-12"><Loader2 className="w-8 h-8 animate-spin text-gray-400" /></div>}
        </CardContent>
      </Card>

      {/* Sign-Up Analytics Pie Chart */}
      {signupAnalytics && (
        <Card className={`border-0 shadow-soft rounded-3xl mt-6 ${cardBg}`} data-testid="signup-analytics-card">
          <CardHeader>
            <CardTitle className={`flex items-center gap-2 ${darkMode ? "text-gray-100" : ""}`}><Users className={`w-5 h-5 ${darkMode ? "text-blue-400" : "text-primary"}`} /> User Sign-Up Analytics</CardTitle>
            <p className={`text-sm mt-1 ${darkMode ? "text-gray-400" : "text-muted-foreground"}`}>Registration activity breakdown by time period</p>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <div className="flex items-center justify-center">
                <ResponsiveContainer width="100%" height={320}>
                  <PieChart>
                    <Pie data={[
                      { name: "Last Week (LWSU)", value: signupAnalytics.summary.last_week, fill: "#e0482d" },
                      { name: "Last Month (LMSU)", value: Math.max(0, signupAnalytics.summary.last_month - signupAnalytics.summary.last_week), fill: "#f59e0b" },
                      { name: "Last Year (LYSU)", value: Math.max(0, signupAnalytics.summary.last_year - signupAnalytics.summary.last_month), fill: "#3b82f6" },
                      { name: "Older", value: signupAnalytics.summary.older_than_year, fill: "#6b7280" },
                    ].filter(d => d.value > 0)} cx="50%" cy="50%" innerRadius={60} outerRadius={110} paddingAngle={3} dataKey="value">
                      {[{ fill: "#e0482d" }, { fill: "#f59e0b" }, { fill: "#3b82f6" }, { fill: "#6b7280" }].filter((_, i) => [signupAnalytics.summary.last_week, Math.max(0, signupAnalytics.summary.last_month - signupAnalytics.summary.last_week), Math.max(0, signupAnalytics.summary.last_year - signupAnalytics.summary.last_month), signupAnalytics.summary.older_than_year][i] > 0).map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.fill} />
                      ))}
                    </Pie>
                    <Tooltip contentStyle={{ backgroundColor: darkMode ? "#1f2937" : "#fff", border: `1px solid ${darkMode ? "#4b5563" : "#e5e7eb"}`, borderRadius: "12px", color: darkMode ? "#f3f4f6" : "#111827" }} itemStyle={{ color: darkMode ? "#e5e7eb" : "#111827" }} labelStyle={{ color: darkMode ? "#f9fafb" : "#111827", fontWeight: 600 }} formatter={(value) => [`${value} users`, ""]} />
                    <Legend />
                  </PieChart>
                </ResponsiveContainer>
              </div>
              <div className="grid grid-cols-2 gap-3">
                {[
                  { label: "Last Week", abbr: "LWSU", value: signupAnalytics.summary.last_week, color: "bg-red-500", textColor: "text-red-600" },
                  { label: "Last Month", abbr: "LMSU", value: signupAnalytics.summary.last_month, color: "bg-amber-500", textColor: "text-amber-600" },
                  { label: "Last Year", abbr: "LYSU", value: signupAnalytics.summary.last_year, color: "bg-blue-500", textColor: "text-blue-600" },
                  { label: "All Time", abbr: "Total", value: signupAnalytics.summary.total, color: "bg-gray-500", textColor: "text-gray-600" },
                ].map((item) => (
                  <div key={item.abbr} className={`rounded-2xl p-4 ${darkMode ? "bg-gray-700/50" : "bg-gray-50"}`} data-testid={`signup-stat-${item.abbr.toLowerCase()}`}>
                    <div className="flex items-center gap-2 mb-2"><div className={`w-3 h-3 rounded-full ${item.color}`} /><span className={`text-xs font-medium ${darkMode ? "text-gray-300" : "text-gray-500"}`}>{item.abbr}</span></div>
                    <p className={`text-2xl font-bold ${item.textColor}`}>{item.value}</p>
                    <p className={`text-xs ${darkMode ? "text-gray-400" : "text-gray-500"}`}>{item.label}</p>
                  </div>
                ))}
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* User Role Distribution Bar Chart */}
      {roleAnalytics && roleAnalytics.chart_data?.length > 0 && (
        <Card className={`border-0 shadow-soft rounded-3xl mt-6 ${cardBg}`} data-testid="role-analytics-card">
          <CardHeader>
            <CardTitle className={`flex items-center gap-2 ${darkMode ? "text-gray-100" : ""}`}>
              <Users className={`w-5 h-5 ${darkMode ? "text-blue-400" : "text-primary"}`} /> User Role Distribution
            </CardTitle>
            <p className={`text-sm mt-1 ${darkMode ? "text-gray-400" : "text-muted-foreground"}`}>
              Accounts created by role — {roleAnalytics.total_users} total users
            </p>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              <div className="lg:col-span-2">
                <ResponsiveContainer width="100%" height={300}>
                  <ComposedChart data={roleAnalytics.chart_data} margin={{ top: 5, right: 20, left: 0, bottom: 5 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke={darkMode ? "#374151" : "#e5e7eb"} />
                    <XAxis dataKey="label" tick={{ fontSize: 12, fill: darkMode ? "#9ca3af" : "#6b7280" }} axisLine={{ stroke: darkMode ? "#4b5563" : "#d1d5db" }} interval={0} />
                    <YAxis tick={{ fontSize: 12, fill: darkMode ? "#9ca3af" : "#6b7280" }} axisLine={{ stroke: darkMode ? "#4b5563" : "#d1d5db" }} allowDecimals={false} />
                    <Tooltip contentStyle={{ backgroundColor: darkMode ? "#1f2937" : "#fff", border: `1px solid ${darkMode ? "#4b5563" : "#e5e7eb"}`, borderRadius: "12px", color: darkMode ? "#f3f4f6" : "#111827" }} itemStyle={{ color: darkMode ? "#e5e7eb" : "#111827" }} labelStyle={{ color: darkMode ? "#f9fafb" : "#111827", fontWeight: 600 }} formatter={(value) => [`${value} users`, "Count"]} />
                    <Bar dataKey="count" radius={[8, 8, 0, 0]} maxBarSize={60}>
                      {roleAnalytics.chart_data.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={{"student":"#3b82f6","teacher":"#10b981","parent":"#8b5cf6","school_admin":"#f59e0b","pending":"#94a3b8","sub_admin":"#ec4899","admin":"#ef4444"}[entry.role] || "#6b7280"} />
                      ))}
                    </Bar>
                  </ComposedChart>
                </ResponsiveContainer>
              </div>
              <div className="space-y-3">
                {roleAnalytics.chart_data.map((item) => (
                  <div key={item.role} className={`flex items-center justify-between rounded-xl p-3 ${darkMode ? "bg-gray-700/50" : "bg-gray-50"}`} data-testid={`role-stat-${item.role}`}>
                    <div className="flex items-center gap-3">
                      <div className="w-3 h-3 rounded-full" style={{ backgroundColor: {"student":"#3b82f6","teacher":"#10b981","parent":"#8b5cf6","school_admin":"#f59e0b","pending":"#94a3b8","sub_admin":"#ec4899","admin":"#ef4444"}[item.role] || "#6b7280" }} />
                      <span className={`text-sm font-medium ${darkMode ? "text-gray-200" : "text-gray-700"}`}>{item.label}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className={`text-lg font-bold ${darkMode ? "text-white" : "text-gray-900"}`}>{item.count}</span>
                      <span className={`text-xs ${darkMode ? "text-gray-400" : "text-gray-500"}`}>({roleAnalytics.total_users > 0 ? ((item.count / roleAnalytics.total_users) * 100).toFixed(0) : 0}%)</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </CardContent>
        </Card>
      )}
    </>
  );
};
